import type { z } from "zod";

import type { Result } from "@/common/helpers/result";
import { err, ok } from "@/common/helpers/result";

export type InferModelType<S extends { schema: z.ZodType }> = z.output<S["schema"]>;

// biome-ignore lint/complexity/noBannedTypes: fine as the default value.
export function createModel<S extends z.ZodType, const T extends object = {}>(
  schema: S,
  extend?: (model: Model<S>) => T,
) {
  const model: Model<S> = {
    schema,
    is: (value): value is z.output<S> => schema.safeParse(value).success,
    from: (value) => schema.parse(value),
    cast: (value) => schema.parse(value),
    safeFrom: (value) => {
      const result = schema.safeParse(value);

      return result.success ? ok(result.data) : err(result.error);
    },
    safeCast: (value) => {
      const result = schema.safeParse(value);

      return result.success ? ok(result.data) : err(result.error);
    },
  };

  return Object.freeze(extend ? { ...model, ...extend(model) } : model) as Model<S> & T;
}

export type Model<S extends z.ZodType> = {
  /**
   * Schema used to validate the model.
   *
   * Use it only when composing models together. For data validation use model methods.
   */
  readonly schema: S;

  /**
   * Checks if the value matches the model schema.
   */
  readonly is: (value: unknown) => value is z.output<S>;

  /**
   * Parses value matching the model input type to the model type.
   * @throws if the value doesn't match the model schema.
   */
  readonly from: (value: z.input<S>) => z.output<S>;

  /**
   * Parses value matching the model input type to the model type.
   * Returns a result instead of throwing an error.
   */
  readonly safeFrom: (value: z.input<S>) => Result<z.output<S>, z.ZodError<z.output<S>>>;

  /**
   * Parses any value to the model type.
   * @throws if the value doesn't match the model schema.
   */
  readonly cast: (value?: unknown) => z.output<S>;

  /**
   * Parses any value to the model type.
   * Returns a result instead of throwing an error.
   */
  readonly safeCast: (value?: unknown) => Result<z.output<S>, z.ZodError<z.output<S>>>;
};
